import React from 'react'
import Input from '../forms/input'
import { IValueEditorProps } from '@app-types/general'
import { createLogger } from '@utils/logger'
import useValueEditor from '@hooks/useValueEditor'

// Initialize logger
const log = createLogger('StringEditor', { prependScope: true })
log.setLevel('info')

// Define interfaces for props and related data structures

type ChangeObject = {
  name?: string
  value: string
}

type StringEditorProps = Omit<IValueEditorProps, 'serializeValue' | 'parseValue'> & {
  onDone?: () => void // if we want to decide when to save in parent component
  serializeValue?: (value: string | null) => string | null
  parseValue?: (value: string | null) => string | null
}

const passthrough = (value: string | null): string | null => value
const defaultParseValueFn = passthrough
const defaultSerializeValueFn = passthrough

const StringEditor: React.FC<StringEditorProps> & { EDITOR_TYPE: string } = ({
  name,
  value = '',
  onSave,
  onDone,
  onChange,
  onEditCancel,
  parseValue = defaultParseValueFn,
  serializeValue = defaultSerializeValueFn,
  ...restProps
}) => {
  // Warn if onDone and onSave are both passed
  // When we pass onSave handler, we expect this component to decide when it should call onSave (to save the value)
  // When we pass onDone handler, we expect the parent component to decide when it's appropriate to save the value
  if (onDone && onSave) {
    log.warn('onDone and onSave are both passed as props. This is not recommended.')
  }

  const { localValue, handleLocalChange, handleBlurOrSave } = useValueEditor<string | null, string | null>({
    value,
    parseValue,
    serializeValue,
    onChange: onChange ? ({ value }) => onChange({ name, value }) : undefined,
    onSave,
    onEditCancel,
  })

  const handleBlur = () => {
    onDone?.()
    handleBlurOrSave()
  }

  // trigger blur event when enter key is pressed, which in turn will call our handleBlur
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      ;(event.target as HTMLInputElement).blur() // Trigger onBlur callback
    }
  }

  const handleChange = (changeObj: ChangeObject) => {
    handleLocalChange(changeObj.value)
  }

  const normalizedValue = localValue ?? ''
  return (
    <span className="content-editor content-editor--string string-editor">
      <Input
        {...restProps}
        name={name}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        value={normalizedValue}
      />
    </span>
  )
}

StringEditor.EDITOR_TYPE = 'string'

export default StringEditor
